import type { Moment } from 'moment';
import { TimePresetContext } from 'modules/iterableGoals/types';
import { WorkflowState } from 'modules/workflows/ducks';
import { Workflow } from 'modules/workflows/types';
import { getDefaultMetricsDateRange } from 'modules/workflows/utils/dataUtils/sendMetricsUtils';
import { DateType } from 'ui/datePicker';
import { RELATIVE_DATE_PRESETS } from 'ui/datePicker/helpers';
import { Preset } from './FlexMetricsRangeSelect.types';

export const getTriggerText = (start: DateType, end: DateType) =>
  `${start?.format('MM/DD/YY')} - ${end?.format('MM/DD/YY')}`;

export const getPresets = (createdAt: string, updatedAt: string): Record<Preset, any> => ({
  ...RELATIVE_DATE_PRESETS,
  [Preset.Today]: {
    ...RELATIVE_DATE_PRESETS[Preset.Today],
    filterLabel: 'From today',
  },
  [Preset.PastThreeDays]: {
    ...RELATIVE_DATE_PRESETS[Preset.PastThreeDays],
    filterLabel: 'Past 3 days',
  },
  [Preset.PastSevenDays]: {
    ...RELATIVE_DATE_PRESETS[Preset.PastSevenDays],
    filterLabel: 'Past 7 days',
  },
  [Preset.PastThirtyDays]: {
    ...RELATIVE_DATE_PRESETS[Preset.PastThirtyDays],
    filterLabel: 'Past 30 days',
  },
  [Preset.JourneyStart]: {
    menuLabel: 'Journey beginning',
    filterLabel: 'Since beginning',
    format: (tz: string) => getDefaultMetricsDateRange(createdAt, tz),
  },
  [Preset.SinceLastPublished]: {
    menuLabel: 'Since last publish date',
    filterLabel: 'Last published',
    format: (tz: string) => getDefaultMetricsDateRange(updatedAt, tz),
  },
});

interface GetPresetFromDateRangeProps {
  // Current date range selected
  dateRange: WorkflowState['flexMetrics'];
  // Current workflow
  workflow: Workflow;
  // Timezone for the workflow
  timeZone?: string;
}

/**
 * Does the date range match the given preset?
 * @param preset The preset to check against
 * @param startDate Start of the date range
 * @param endDate End of the date range
 * @param timeZone Optional timezone to use for checking
 * @returns <boolean> True if the date range matches the preset
 */
const doesDateMatchPreset = (
  preset: TimePresetContext,
  startDate: Moment,
  endDate: Moment,
  timeZone?: string,
): boolean => {
  if (!preset.format) return false;
  const { startDate: presetStart, endDate: presetEnd } = preset.format(timeZone);
  return !!(presetStart?.isSame(startDate, 'day') && presetEnd?.isSame(endDate, 'day'));
};

/**
 * Gets the preset for the date range picker from the currently selected date range.
 */
export const getPresetFromDateRange = ({
  dateRange,
  workflow,
  timeZone,
}: GetPresetFromDateRangeProps): Preset => {
  const { startDate, endDate } = dateRange || {};

  // return the custom preset if the date range is not defined
  if (!startDate || !endDate) return Preset.JourneyStart;

  const presets = getPresets(workflow.createdAt as string, workflow.updatedAt as string);
  const presetEntries = Object.entries(presets);

  // Give priority to the journey start preset if it matches the current date range
  if (doesDateMatchPreset(presets[Preset.JourneyStart], startDate, endDate, timeZone)) {
    return Preset.JourneyStart;
  }

  // Iterate over presents.  If the preset matches the current date range,
  // return that preset key.
  for (let index = 0; index < presetEntries.length; index++) {
    const [presetName, preset] = presetEntries[index];
    // Skip the journey start preset as we have already checked it
    // eslint-disable-next-line no-continue
    if (presetName === Preset.JourneyStart) continue;
    // eslint-disable-next-line no-continue
    if (typeof preset?.format !== 'function') continue;

    if (doesDateMatchPreset(preset, startDate, endDate, timeZone)) {
      return presetName as Preset;
    }
  }

  return Preset.Custom;
};
